import * as Y from "yjs";
import {
  addBlock,
  blocksMap,
  bringToFront,
  createBlock,
  newId,
  nextZIndex,
  readBlock,
  readBlocks,
  removeBlocks,
  sendToBack,
  updateBlock,
  type BlockType,
  type Point,
  type StoredBlock,
} from "@worktools/shared";

let clipboard: StoredBlock[] = [];

export function addBlockAt(doc: Y.Doc, type: BlockType, at: Point, userId: string): string {
  const z = nextZIndex(readBlocks(doc));
  const block = createBlock(type, at, userId, z);
  addBlock(doc, block);
  return block.id;
}

function cloneBlock(doc: Y.Doc, source: StoredBlock, offset: Point, userId: string): string {
  const id = newId(source.type.slice(0, 2));
  const z = nextZIndex(readBlocks(doc));
  addBlock(doc, {
    ...source,
    id,
    x: source.x + offset.x,
    y: source.y + offset.y,
    z,
    createdBy: userId,
    createdAt: Date.now(),
    props: structuredClone(source.props),
  });
  return id;
}

export function duplicateBlocks(doc: Y.Doc, ids: string[], userId: string): string[] {
  const created: string[] = [];
  doc.transact(() => {
    for (const id of ids) {
      const source = readBlock(doc, id);
      if (source) created.push(cloneBlock(doc, source, { x: 24, y: 24 }, userId));
    }
  });
  return created;
}

export function copyBlocks(doc: Y.Doc, ids: string[]): number {
  clipboard = ids.map((id) => readBlock(doc, id)).filter(Boolean) as StoredBlock[];
  return clipboard.length;
}

export function pasteBlocks(doc: Y.Doc, at: Point, userId: string): string[] {
  if (clipboard.length === 0) return [];
  const anchorX = Math.min(...clipboard.map((block) => block.x));
  const anchorY = Math.min(...clipboard.map((block) => block.y));
  const created: string[] = [];
  doc.transact(() => {
    for (const block of clipboard) {
      created.push(cloneBlock(doc, block, { x: at.x - anchorX, y: at.y - anchorY }, userId));
    }
  });
  return created;
}

export function hasClipboard(): boolean {
  return clipboard.length > 0;
}

export function deleteBlocks(doc: Y.Doc, ids: string[]): void {
  removeBlocks(doc, ids);
}

export function setAccent(doc: Y.Doc, ids: string[], accent: string | undefined): void {
  doc.transact(() => {
    for (const id of ids) updateBlock(doc, id, { accent });
  });
}

export function toggleLock(doc: Y.Doc, ids: string[]): void {
  doc.transact(() => {
    for (const id of ids) {
      const map = blocksMap(doc).get(id);
      if (map) map.set("locked", !map.get("locked"));
    }
  });
}

export { bringToFront, sendToBack };
